import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  Download, FileText, TrendingUp, TrendingDown, Users, 
  Calendar, DollarSign, Activity, Filter, FileSpreadsheet, Clock
} from 'lucide-react';
import ScheduledReportsManager from './ScheduledReportsManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0D9488', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];

const ReportsTab = () => {
  const [dateRange, setDateRange] = useState('last_30_days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [activeReport, setActiveReport] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);

  const getDateRange = () => {
    const now = new Date();
    let start, end = now.toISOString();

    switch (dateRange) {
      case 'last_7_days':
        start = new Date(now.setDate(now.getDate() - 7)).toISOString();
        break;
      case 'last_30_days':
        start = new Date(now.setDate(now.getDate() - 30)).toISOString();
        break;
      case 'last_90_days':
        start = new Date(now.setDate(now.getDate() - 90)).toISOString();
        break;
      case 'custom':
        start = customStart;
        end = customEnd;
        break;
      default:
        start = new Date(now.setDate(now.getDate() - 30)).toISOString();
    }

    return { start, end };
  };

  useEffect(() => {
    fetchAnalyticsOverview();
  }, []);

  const fetchAnalyticsOverview = async () => {
    try {
      const response = await axios.get(`${API}/reports/analytics/overview`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const generateReport = async (reportType) => {
    setLoading(true);
    setActiveReport(reportType);

    try {
      const { start, end } = getDateRange();
      const response = await axios.post(`${API}/reports/${reportType}`, {
        report_type: reportType,
        start_date: start,
        end_date: end,
        filters: {}
      });

      setReportData(response.data);
      toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report generated`);
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async (reportType) => {
    try {
      const { start, end } = getDateRange();
      const response = await axios.post(`${API}/reports/export/csv`, {
        report_type: reportType,
        start_date: start,
        end_date: end,
        filters: {}
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('CSV report exported successfully');
    } catch (error) {
      console.error('Failed to export report:', error);
      toast.error('Failed to export CSV');
    }
  };

  const exportExcel = async (reportType) => {
    try {
      const { start, end } = getDateRange();
      const response = await axios.post(`${API}/reports/export/excel`, {
        report_type: reportType,
        start_date: start,
        end_date: end,
        filters: {}
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Excel report exported successfully');
    } catch (error) {
      console.error('Failed to export report:', error);
      toast.error('Failed to export Excel');
    }
  };

  const exportPDF = async (reportType) => {
    try {
      const { start, end } = getDateRange();
      const response = await axios.post(`${API}/reports/export/pdf`, {
        report_type: reportType,
        start_date: start,
        end_date: end,
        filters: {}
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF report exported successfully');
    } catch (error) {
      console.error('Failed to export report:', error);
      toast.error('Failed to export PDF');
    }
  };

  const reportTypes = [
    { id: 'overview', label: 'Overview', icon: Activity, color: 'teal' },
    { id: 'revenue', label: 'Revenue', icon: DollarSign, color: 'green' },
    { id: 'membership', label: 'Membership', icon: Users, color: 'blue' },
    { id: 'appointments', label: 'Appointments', icon: Calendar, color: 'purple' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format data for charts
  const getChartData = () => {
    if (!reportData) return null;

    if (activeReport === 'revenue' && reportData.daily_breakdown) {
      return Object.entries(reportData.daily_breakdown).map(([date, amount]) => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        amount
      }));
    }

    if (activeReport === 'membership' && reportData.by_plan) {
      return Object.entries(reportData.by_plan).map(([name, value]) => ({
        name,
        value
      }));
    }

    if (activeReport === 'appointments' && reportData.by_status) {
      return Object.entries(reportData.by_status).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
      }));
    }

    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Reports & Analytics</h2>
          <p className="text-gray-600 mt-1">Comprehensive insights into your practice performance</p>
        </div>
      </div>

      {/* Tabs for Reports and Scheduled Reports */}
      <Tabs defaultValue="reports" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Reports
          </TabsTrigger>
          <TabsTrigger value="scheduled" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Scheduled
          </TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="space-y-6 mt-6">
          {/* Date Range Selector */}
          <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-lg">Date Range</CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Select Period</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="last_90_days">Last 90 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {dateRange === 'custom' && (
              <>
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Start Date</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">End Date</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Analytics Overview */}
      {analyticsData && activeReport === 'overview' && (
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader>
              <CardDescription>Revenue (30 days)</CardDescription>
              <CardTitle className="text-3xl">{formatCurrency(analyticsData.revenue.current)}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                {analyticsData.revenue.change_percent >= 0 ? (
                  <TrendingUp className="h-5 w-5 text-green-600" />
                ) : (
                  <TrendingDown className="h-5 w-5 text-red-600" />
                )}
                <span className={`font-semibold ${analyticsData.revenue.change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {Math.abs(analyticsData.revenue.change_percent).toFixed(1)}%
                </span>
                <span className="text-sm text-gray-600">vs previous period</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader>
              <CardDescription>Active Members</CardDescription>
              <CardTitle className="text-3xl">{analyticsData.members.active}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span className="text-sm text-gray-600">
                  {analyticsData.members.new_this_period} new this period
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader>
              <CardDescription>Appointments (30 days)</CardDescription>
              <CardTitle className="text-3xl">{analyticsData.appointments.total}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                <span className="text-sm text-gray-600">
                  {analyticsData.appointments.completed} completed
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Report Type Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Report Templates</CardTitle>
          <CardDescription>Select a report type to view detailed analytics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4">
            {reportTypes.map((report) => {
              const Icon = report.icon;
              const isActive = activeReport === report.id;
              
              return (
                <button
                  key={report.id}
                  onClick={() => {
                    if (report.id === 'overview') {
                      setActiveReport('overview');
                      setReportData(null);
                    } else {
                      generateReport(report.id);
                    }
                  }}
                  className={`p-6 rounded-lg border-2 transition-all text-left ${
                    isActive 
                      ? 'border-teal-500 bg-teal-50' 
                      : 'border-gray-200 hover:border-teal-300 hover:bg-gray-50'
                  }`}
                >
                  <Icon className={`h-8 w-8 mb-3 ${isActive ? 'text-teal-600' : 'text-gray-600'}`} />
                  <h3 className="font-semibold text-gray-900 mb-1">{report.label}</h3>
                  {isActive && <Badge className="bg-teal-600">Active</Badge>}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {loading && (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="animate-spin h-12 w-12 border-4 border-teal-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-600 mt-4">Generating report...</p>
          </CardContent>
        </Card>
      )}

      {reportData && !loading && (
        <>
          {/* Summary Card */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="capitalize">{activeReport} Report</CardTitle>
                  <CardDescription>
                    {reportData.period?.start && new Date(reportData.period.start).toLocaleDateString()} - {reportData.period?.end && new Date(reportData.period.end).toLocaleDateString()}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportPDF(activeReport)}
                    className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportExcel(activeReport)}
                    className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Excel
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportCSV(activeReport)}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary Stats */}
              {reportData.summary && (
                <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                  {Object.entries(reportData.summary).map(([key, value]) => (
                    <div key={key} className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      <div className="text-2xl font-bold text-gray-900">
                        {typeof value === 'number' && key.includes('rate') ? `${value}%` :
                         typeof value === 'number' && (key.includes('revenue') || key.includes('transaction')) ? formatCurrency(value) :
                         value}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Charts */}
              {getChartData() && (
                <div className="space-y-8">
                  {activeReport === 'revenue' && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Revenue Trend</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={getChartData()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Line type="monotone" dataKey="amount" stroke="#0D9488" strokeWidth={2} name="Revenue" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {activeReport === 'membership' && (
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Members by Plan</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={getChartData()}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={(entry) => `${entry.name}: ${entry.value}`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {getChartData().map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      {reportData.status_breakdown && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Breakdown</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={Object.entries(reportData.status_breakdown).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }))}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#3B82F6" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  )}

                  {activeReport === 'appointments' && (
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Appointments by Status</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={getChartData()}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={(entry) => `${entry.name}: ${entry.value}`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {getChartData().map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      {reportData.by_type && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">By Type</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={Object.entries(reportData.by_type).map(([name, value]) => ({ name, value }))}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#8B5CF6" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
        </TabsContent>

        <TabsContent value="scheduled" className="mt-6">
          <ScheduledReportsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsTab;
